import React from "react";
import { resolveAnyStaticUrl } from "../../urlConfig";

const formatMoney = (value, currency = "RUB") => {
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: currency || "RUB",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return `${Number(value || 0).toFixed(2)} ${currency || "RUB"}`;
  }
};

const formatDate = (value) => {
  if (!value) return "Дата не указана";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const hasClient = (client) =>
  Boolean(
    client?.name ||
      client?.phone ||
      client?.email ||
      client?.address
  );

const OfferPreview = ({ offer }) => {
  const currency = offer?.currency || "RUB";
  const items = offer?.items || [];
  const meta = offer?.meta || {};
  const documentHeading =
    meta.documentHeading || "КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ";
  const documentDateText = meta.documentDateText || "";
  const recipientVisible =
    meta.showRecipient === true && Boolean(meta.recipientText);
  const senderVisible =
    meta.showSender === true && Boolean(meta.senderText);
  const signatureVisible = meta.showSignature === true;
  const conditions = [
    ["Срок выполнения", meta.delivery],
    ["Условия оплаты", meta.payment],
    ["Гарантия", meta.warranty],
    ["Предложение действительно", meta.validity],
  ].filter(([, value]) => Boolean(value));

  return (
    <div className="offer-preview">
      <div className="offer-preview__toolbar">
        <div>
          <span className="offer-preview__dot" />
          Предпросмотр A4
        </div>
        <small>Итоговая верстка формируется на сервере</small>
      </div>

      <div className="offer-preview__paper">
        {(recipientVisible || senderVisible) && (
          <section className="offer-preview__addressing">
            {recipientVisible && (
              <div>
                <span>{meta.recipientLabel || "Кому"}:</span>
                <strong>{meta.recipientText}</strong>
              </div>
            )}
            {senderVisible && (
              <div>
                <span>{meta.senderLabel || "От"}:</span>
                <strong>{meta.senderText}</strong>
              </div>
            )}
          </section>
        )}

        <header className="offer-preview__header">
          <div>
            <span className="offer-preview__brand">
              {offer?.company || "ALOTB.RU"}
            </span>
            <span className="offer-preview__document-label">
              {[documentHeading, documentDateText].filter(Boolean).join(" ")}
            </span>
          </div>

          <div className="offer-preview__document-meta">
            <strong>№ {offer?.docNumber || "—"}</strong>
            {!documentDateText && <span>{formatDate(offer?.date)}</span>}
          </div>
        </header>

        <section className="offer-preview__hero">
          <span>Предложение для вашего проекта</span>
          <h2>{offer?.title || "Коммерческое предложение"}</h2>
          {offer?.description && <p>{offer.description}</p>}
        </section>

        {hasClient(offer?.client) && (
          <section className="offer-preview__client">
            <span>Заказчик</span>
            <strong>{offer.client?.name || "Не указан"}</strong>
            <p>
              {[offer.client?.phone, offer.client?.email, offer.client?.address]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </section>
        )}

        {conditions.length > 0 && (
          <section className="offer-preview__conditions">
            {conditions.map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </section>
        )}

        <section className="offer-preview__products">
          <div className="offer-preview__section-title">
            <span>Состав предложения</span>
            <strong>{items.length}</strong>
          </div>

          <div className="offer-preview__items">
            {items.length ? (
              items.map((item, index) => {
                const showImage =
                  item.meta?.showImage !== false && item.imageUrl;
                const productLink =
                  item.meta?.showProductLink && item.meta?.productUrl;
                const sceneLink =
                  item.meta?.showSceneLink && item.meta?.sceneUrl;

                return (
                  <article
                    className="offer-preview__item"
                    key={String(item.itemId || item.productId || index)}
                  >
                    {showImage && (
                      <img
                        src={resolveAnyStaticUrl(item.imageUrl)}
                        alt=""
                      />
                    )}

                    <div className="offer-preview__item-content">
                      <span className="offer-preview__item-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h3>{item.name || "Без названия"}</h3>
                      {item.meta?.details && <p>{item.meta.details}</p>}
                      {item.meta?.dimensions && (
                        <small>{item.meta.dimensions}</small>
                      )}

                      {(productLink || sceneLink) && (
                        <div className="offer-preview__links">
                          {productLink && <span>Товар ↗</span>}
                          {sceneLink && <span>3D-сцена ↗</span>}
                        </div>
                      )}
                    </div>

                    <div className="offer-preview__item-price">
                      <small>
                        {item.qty || 0} {item.meta?.unit || "шт."} ×{" "}
                        {formatMoney(item.price, currency)}
                      </small>
                      <strong>
                        {formatMoney(
                          Number(item.qty || 0) * Number(item.price || 0),
                          currency
                        )}
                      </strong>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="offer-preview__empty">
                Позиции появятся здесь после добавления.
              </div>
            )}
          </div>
        </section>

        <section className="offer-preview__total">
          <span>Итоговая стоимость</span>
          <strong>
            {formatMoney(offer?.totals?.grandTotal || 0, currency)}
          </strong>
          {offer?.totals?.grandTotalText && (
            <small>{offer.totals.grandTotalText}</small>
          )}
        </section>

        {meta.extra && (
          <section className="offer-preview__note">
            <strong>Дополнительные условия</strong>
            <p>{meta.extra}</p>
          </section>
        )}

        {signatureVisible && (
          <section className="offer-preview__signature">
            <div>
              <span>{meta.signaturePosition || "Исполнитель"}</span>
              <strong>{meta.signatureName || offer?.company || ""}</strong>
            </div>
            <div className="offer-preview__signature-line">
              <span>{meta.signatureLabel || "Подпись"}</span>
            </div>
          </section>
        )}

        <footer className="offer-preview__footer">
          <span>{offer?.company || "ALOTB.RU"}</span>
          <span>Коммерческое предложение № {offer?.docNumber || "—"}</span>
        </footer>
      </div>
    </div>
  );
};

export default OfferPreview;
